import { useEffect, useState } from "react";
import { useAppSelector } from "../redux/hooks";

const EMPTY_IDS = {};

export function reconcileImageDescriptions(
  descriptions: Record<string, string>,
  files: any[],
  resolvedIds: Record<string, string>,
) {
  let next = descriptions;
  for (const alias of Object.keys(descriptions)) {
    const file = files.find(
      (item) =>
        item._clientId &&
        [item._clientId, `pending_${item._clientId}`].includes(alias),
    );
    const actualId = resolvedIds[alias] || file?._id;
    if (!actualId || actualId === alias) continue;
    if (next === descriptions) next = { ...descriptions };
    // A newer edit already entered under the real ID takes precedence,
    // including an intentionally empty description.
    if (!Object.prototype.hasOwnProperty.call(next, actualId))
      next[actualId] = descriptions[alias];
    delete next[alias];
  }
  // Captions belong only to images still present in this section. Keeping a
  // deleted key here resubmits it alongside the next image's description.
  const liveIds = new Set(
    files
      .flatMap((file) => [
        file._id,
        file.id,
        file._clientId,
        ...(file._clientId ? [`pending_${file._clientId}`] : []),
      ])
      .filter(Boolean),
  );
  for (const id of Object.keys(next)) {
    if (liveIds.has(id)) continue;
    if (next === descriptions) next = { ...descriptions };
    delete next[id];
  }
  return next;
}

export function useImageDescriptions(files: any[]) {
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const resolvedIds = useAppSelector(
    (state) => state.offline?.resolvedIds || EMPTY_IDS,
  );
  useEffect(() => {
    setDescriptions((previous) =>
      reconcileImageDescriptions(previous, files, resolvedIds),
    );
  }, [files, resolvedIds]);
  return [descriptions, setDescriptions] as const;
}
